import { useSyncExternalStore } from 'react';
import { language, onLanguageChange, t, translateError, type Language } from './index';

/**
 * Re-renders a component when the language changes.
 *
 * <p>`useSyncExternalStore` rather than context: the language is read in almost every
 * component, and threading a provider through every screen to deliver a value that changes
 * twice a year is more plumbing than the problem deserves.</p>
 */
export function useLanguage(): Language {
  return useSyncExternalStore(onLanguageChange, language, language);
}

/** The translator, bound to the current language so a change re-renders the caller. */
export function useTranslate() {
  useLanguage();
  return t;
}

/**
 * Turns whatever a failed request threw into a sentence.
 *
 * <p>Takes `unknown` because that is what a `catch` gives, and shapes rather than classes
 * because the only thing that matters is whether there is a code to look up.</p>
 */
export function describeError(error: unknown, fallbackKey: string): string {
  const shaped = error as { code?: string | null; message?: string; args?: Record<string, string> };

  if (shaped?.code) {
    return translateError(shaped.code, shaped.message ?? t(fallbackKey), shaped.args);
  }

  return t(fallbackKey);
}
